import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  ConfirmDeliveryLinePayload,
  DeliveryLineFoundBy,
  DeliveryStatus,
  Location,
  ScheduledDelivery,
  ScheduledDeliveryLine,
  Supplier,
  UpdateScheduledDeliveryPayload,
} from "../api/types";
import { DeliveryDocumentsBlock } from "../components/deliveries/DeliveryDocumentsBlock";
import { DeliveryScanBox } from "../components/deliveries/DeliveryScanBox";
import { linesWithFoundFirst } from "../utils/findDeliveryLine";
import { DeliveryLinesEditor } from "../components/DeliveryLinesEditor";
import {
  deliveryLinePayload,
  newDeliveryLineDraft,
  type DeliveryLineDraft,
} from "../utils/deliveryLineDrafts";
import { ConfirmDeliveryLineRow } from "../components/ConfirmDeliveryLineRow";
import { dateText, money } from "../i18n/intl";
import { ReasonModal } from "../components/ui/ReasonModal";
import { useAuth } from "../state/AuthContext";
import { deliveryHeadline, deliveryTotals } from "../utils/deliveryTotals";
import { basisForPrefill } from "../utils/typedMoneyBasis";

const fmtDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

function statusBadge(status: DeliveryStatus) {
  const map: Record<DeliveryStatus, string> = {
    SCHEDULED: "badge muted",
    IN_PROGRESS: "badge primary",
    COMPLETED: "badge success",
    CANCELED: "badge danger",
  };
  return <span className={map[status]}>{status.replace("_", " ")}</span>;
}

function toLocalDateTimeInput(iso: string) {
  const d = new Date(iso);
  const offsetMs = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function DeliveryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [delivery, setDelivery] = useState<ScheduledDelivery | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // edit form state
  const [editSupplierId, setEditSupplierId] = useState("");
  const [editScheduledFor, setEditScheduledFor] = useState("");
  const [editInvoice, setEditInvoice] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editLines, setEditLines] = useState<DeliveryLineDraft[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const d = await api.getScheduledDelivery(id);
      setDelivery(d);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load delivery");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    void api
      .listSuppliers()
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
  }, []);

  const isOwnerOrManager =
    user?.role === "OWNER" || user?.role === "MANAGER";
  const canEdit =
    isOwnerOrManager && delivery?.status === "SCHEDULED";
  const canCancel =
    isOwnerOrManager &&
    delivery !== null &&
    (delivery.status === "SCHEDULED" || delivery.status === "IN_PROGRESS") &&
    delivery.lines.every((l) => l.status === "PENDING");
  // G-164 (D-85): only while nothing has been received (the server's `assertDeliveryMovable`).
  const canMove =
    user?.role === "OWNER" &&
    delivery !== null &&
    delivery.status === "SCHEDULED" &&
    delivery.lines.every((l) => l.status === "PENDING");
  const canConfirmLines = useMemo(() => {
    if (!delivery || !user) return false;
    if (delivery.status === "CANCELED" || delivery.status === "COMPLETED") {
      return false;
    }
    return true;
  }, [delivery, user]);

  const startEdit = () => {
    if (!delivery) return;
    setEditSupplierId(delivery.supplier.id);
    setEditScheduledFor(toLocalDateTimeInput(delivery.scheduledFor));
    setEditInvoice(delivery.invoiceNumber ?? "");
    setEditNotes(delivery.notes ?? "");
    setEditLines(
      delivery.lines.length > 0
        ? delivery.lines.map((l) => ({
            uid: l.id,
            itemId: l.itemId,
            zoneId: l.zoneId,
            expectedQty: String(Number(l.expectedQty)),
            expectedUnitCostEur: String(Number(l.expectedUnitCostEur)),
            // G-121 S3: the stored unit is before IVA. Saved unchanged, the
            // server keeps the line's own facts (invoice or typed) as they are.
            moneyBasis: basisForPrefill(l.expectedUnitCostEur),
          }))
        : [newDeliveryLineDraft()],
    );
    setEditError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditError(null);
  };

  const saveEdit = async () => {
    if (!delivery) return;
    setSaving(true);
    setEditError(null);
    try {
      const payload: UpdateScheduledDeliveryPayload = {
        supplierId: editSupplierId,
        scheduledFor: new Date(editScheduledFor).toISOString(),
        invoiceNumber: editInvoice.trim() || null,
        notes: editNotes.trim() || null,
        lines: editLines.map(deliveryLinePayload),
      };
      const updated = await api.updateScheduledDelivery(delivery.id, payload);
      setDelivery(updated);
      setEditing(false);
    } catch (err: unknown) {
      setEditError(err instanceof Error ? err.message : "Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  // G-131: Cancel asks in the app's own dialog (it was a native confirm), and
  // says what happens to the paper the delivery came from (D-86).
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const cancelDelivery = async () => {
    if (!delivery) return;
    setCanceling(true);
    setCancelError(null);
    try {
      const updated = await api.cancelScheduledDelivery(delivery.id);
      setDelivery(updated);
      setCancelOpen(false);
    } catch (err: unknown) {
      setCancelError(err instanceof Error ? err.message : "Could not cancel delivery");
    } finally {
      setCanceling(false);
    }
  };

  // G-164 (D-85): the owner moves a delivery nothing was received from to another shop.
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveShops, setMoveShops] = useState<Location[]>([]);
  const [moveTo, setMoveTo] = useState("");
  const [moveBusy, setMoveBusy] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const openMove = async () => {
    setMoveError(null);
    setMoveTo("");
    setMoveOpen(true);
    try {
      setMoveShops(await api.listLocations());
    } catch (err: unknown) {
      setMoveError(err instanceof Error ? err.message : "Could not load the shops");
    }
  };
  const moveDelivery = async () => {
    if (!delivery) return;
    if (!moveTo) {
      setMoveError("Pick the shop.");
      return;
    }
    setMoveBusy(true);
    setMoveError(null);
    try {
      setDelivery(await api.moveDelivery(delivery.id, moveTo));
      setMoveOpen(false);
    } catch (err: unknown) {
      setMoveError(err instanceof Error ? err.message : "Could not move the delivery");
    } finally {
      setMoveBusy(false);
    }
  };

  // G-131: undo one line's receiving (owner, manager) while its stock is untouched.
  const [undoLine, setUndoLine] = useState<ScheduledDeliveryLine | null>(null);
  const [undoBusy, setUndoBusy] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);
  const undoReceive = async (reason: string | undefined) => {
    if (!delivery || !undoLine || !reason) return;
    setUndoBusy(true);
    setUndoError(null);
    try {
      setDelivery(await api.unreceiveDeliveryLine(delivery.id, undoLine.id, reason));
      setUndoLine(null);
    } catch (err: unknown) {
      setUndoError(err instanceof Error ? err.message : "Could not undo the receiving");
    } finally {
      setUndoBusy(false);
    }
  };

  // G-164 (KI-165, owner 2026-10-20): the owner reopens a closed wait, with a reason.
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenBusy, setReopenBusy] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const reopenFacturaWait = async (reason: string | undefined) => {
    if (!delivery || !reason) return;
    setReopenBusy(true);
    setReopenError(null);
    try {
      setDelivery(await api.reopenDeliveryFacturaWait(delivery.id, reason));
      setReopenOpen(false);
    } catch (err: unknown) {
      setReopenError(err instanceof Error ? err.message : "Could not reopen the wait");
    } finally {
      setReopenBusy(false);
    }
  };

  // G-131 (OD-164): the owner says no factura will come for a hand-made delivery.
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissBusy, setDismissBusy] = useState(false);
  const [dismissError, setDismissError] = useState<string | null>(null);
  const dismissFacturaWait = async (reason: string | undefined) => {
    if (!delivery || !reason) return;
    setDismissBusy(true);
    setDismissError(null);
    try {
      setDelivery(await api.dismissDeliveryFacturaWait(delivery.id, reason));
      setDismissOpen(false);
    } catch (err: unknown) {
      setDismissError(err instanceof Error ? err.message : "Could not close the wait");
    } finally {
      setDismissBusy(false);
    }
  };

  // G-59: the line a scan or a typed code found — first in the checklist,
  // highlighted, and its confirmation records how it was found.
  const [found, setFound] = useState<{ lineId: string; how: DeliveryLineFoundBy } | null>(null);
  const [unmatchedCode, setUnmatchedCode] = useState<string | null>(null);
  const orderedLines = useMemo(
    () => (delivery ? linesWithFoundFirst(delivery.lines, found?.lineId ?? null) : []),
    [delivery, found],
  );

  const confirmLine = async (
    lineId: string,
    payload: ConfirmDeliveryLinePayload,
    photo?: File | null,
  ) => {
    if (!delivery) return;
    const foundBy: DeliveryLineFoundBy =
      found?.lineId === lineId ? found.how : "TAPPED";
    const updated = await api.confirmDeliveryLine(
      delivery.id,
      lineId,
      { ...payload, foundBy },
      photo,
    );
    setDelivery(updated);
    if (found?.lineId === lineId) setFound(null);
  };

  const captureLineBarcode = async (lineId: string, barcode: string) => {
    if (!delivery) return;
    const updated = await api.captureDeliveryLineBarcode(delivery.id, lineId, { barcode });
    setDelivery(updated);
  };

  if (loading) {
    return (
      <section style={{ padding: "var(--space-4)" }}>Loading delivery…</section>
    );
  }
  if (error || !delivery) {
    return (
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <div className="alert error">{error ?? "Delivery not found"}</div>
        <button
          type="button"
          className="button plain"
          onClick={() => navigate("/app/deliveries")}
        >
          Back to deliveries
        </button>
      </section>
    );
  }

  // G-121 S2: the server values the delivery; the page only reads its totals.
  const totals = deliveryTotals(delivery);
  const confirmedLines = delivery.lines.filter((line) => line.status !== "PENDING");
  const confirmedBy = [...new Map(
    confirmedLines
      .filter((line) => line.confirmedBy !== null)
      .map((line) => [line.confirmedBy!.id, line.confirmedBy!.fullName]),
  ).values()];
  if (confirmedLines.some((line) => line.confirmedBy === null)) {
    confirmedBy.push("Not recorded");
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">
            Delivery from {delivery.supplier.name}
          </h2>
          <p className="page-subtitle">
            ETA {fmtDate(delivery.scheduledFor)} · {delivery.location.name} (
            {delivery.location.code}) · created by {delivery.createdBy.fullName}
          </p>
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
          {statusBadge(delivery.status)}
          {canEdit && !editing ? (
            <button type="button" className="button tinted" onClick={startEdit}>
              Edit
            </button>
          ) : null}
          {canMove && !editing ? (
            <button type="button" className="button tinted" onClick={() => void openMove()}>
              Move to another shop
            </button>
          ) : null}
          {canCancel ? (
            <button
              type="button"
              className="button danger"
              onClick={() => {
                setCancelError(null);
                setCancelOpen(true);
              }}
              disabled={canceling}
            >
              {canceling ? "Canceling…" : "Cancel delivery"}
            </button>
          ) : null}
        </div>
      </div>

      {delivery.invoice ? (
        <div
          className="card"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "var(--space-3)",
            background: "var(--color-primary-soft)",
          }}
        >
          <div style={{ flex: "1 1 240px", fontSize: 15 }}>
            <strong>Created from invoice</strong>
            <span className="muted" style={{ marginLeft: "var(--space-2)" }}>
              {delivery.invoice.invoiceNumber ?? delivery.invoice.id.slice(0, 8)}
            </span>
          </div>
          {isOwnerOrManager ? (
            <Link
              to={`/app/invoices/${delivery.invoice.id}`}
              className="button tinted"
              style={{ flexShrink: 0 }}
            >
              View invoice
            </Link>
          ) : (
            <span className="muted" style={{ fontSize: 13 }}>
              Ask a manager for invoice details.
            </span>
          )}
        </div>
      ) : null}

      {/* G-131 (OD-163 / OD-164): a delivery typed by hand waits for the factura that bills it. */}
      {isOwnerOrManager &&
      (delivery.facturaWait === "covered" ||
        delivery.facturaWait === "waiting" ||
        delivery.facturaWait === "dismissed") ? (
        <div className={`card dlv-factura${delivery.facturaWait === "waiting" ? " dlv-factura--waiting" : ""}`}>
          {delivery.facturaWait === "covered" && delivery.coveredByFactura ? (
            <p className="inv-section-note">
              <strong>Factura</strong>{" "}
              <Link to={`/app/invoices/${delivery.coveredByFactura.id}`}>{delivery.coveredByFactura.label}</Link>. Its IVA
              counts on the factura; the prices typed here stay the stock cost.
            </p>
          ) : delivery.facturaWait === "dismissed" && delivery.facturaWaitDismissal ? (
            <>
              <p className="inv-section-note">
                <strong>No factura</strong> — closed by {delivery.facturaWaitDismissal.by.fullName} on{" "}
                {dateText(new Date(delivery.facturaWaitDismissal.at))}: {delivery.facturaWaitDismissal.reason}. Its IVA
                counts as paid but not deductible.
              </p>
              {/* G-164 (KI-165, owner 2026-10-20): the owner reopens it; a factura cannot be linked until then. */}
              {user?.role === "OWNER" ? (
                <div className="button-row">
                  <button
                    type="button"
                    className="button tinted"
                    onClick={() => {
                      setReopenError(null);
                      setReopenOpen(true);
                    }}
                  >
                    Reopen
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <p className="inv-section-note">
                <span className="paper-pill paper-pill--warn">Waiting for a factura</span> This delivery was typed by
                hand. When {delivery.supplier.name}&apos;s factura arrives, upload it and link this delivery on the factura&apos;s
                page.
              </p>
              {user?.role === "OWNER" ? (
                <div className="button-row">
                  <button
                    type="button"
                    className="button tinted"
                    disabled={delivery.noFacturaBlockedBy !== null}
                    onClick={() => {
                      setDismissError(null);
                      setDismissOpen(true);
                    }}
                  >
                    No factura will come
                  </button>
                  {/* G-137 (OD-184): the server's reason — no document attached yet. */}
                  {delivery.noFacturaBlockedBy ? (
                    <span className="muted dlv-line-undo">{delivery.noFacturaBlockedBy}</span>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {/* G-137 (OD-183 / OD-184): a delivery typed by hand carries its own paper. */}
      {isOwnerOrManager && user && delivery.invoice === null ? (
        <DeliveryDocumentsBlock
          delivery={delivery}
          currentUserId={user.id}
          isOwner={user.role === "OWNER"}
          onChanged={async () => setDelivery(await api.getScheduledDelivery(delivery.id))}
        />
      ) : null}

      {/* G-121 S2: the supplier's billed total leads when every line has one;
          otherwise the expected value, labelled "(typed)" only when no invoice
          stands behind the delivery. What did not arrive is "not received" —
          information, not a receivable. */}
      <div className="card" style={{ display: "flex", gap: "var(--space-5)", flexWrap: "wrap" }}>
        {(() => {
          const lead = deliveryHeadline(totals, delivery.invoice !== null);
          return <Stat label={lead.label} value={money(lead.amount)} />;
        })()}
        {totals.confirmedLines > 0 ? (
          <Stat label="Arrived" value={money(totals.arrivedEur)} />
        ) : null}
        {totals.shortageEur > 0 ? (
          <Stat label="Not received" value={money(totals.shortageEur)} alarm />
        ) : null}
        {totals.overageEur > 0 ? (
          <Stat label="Extra" value={money(totals.overageEur)} />
        ) : null}
        <Stat
          label="Confirmed by"
          value={confirmedBy.length > 0 ? confirmedBy.join(", ") : "Awaiting confirmation"}
        />
        {delivery.invoiceNumber ? (
          <Stat label="Invoice #" value={delivery.invoiceNumber} />
        ) : null}
      </div>

      {delivery.notes ? (
        <div className="card">
          <strong>Notes</strong>
          <p style={{ margin: "var(--space-2) 0 0" }}>{delivery.notes}</p>
        </div>
      ) : null}

      {editing ? (
        <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <h3 style={{ margin: 0 }}>Edit delivery</h3>
          {editError ? <div className="alert error">{editError}</div> : null}
          <div className="form-grid">
            <div className="field">
              <label htmlFor="edit-supplier">Supplier *</label>
              <select
                id="edit-supplier"
                value={editSupplierId}
                onChange={(e) => setEditSupplierId(e.target.value)}
              >
                <option value="">Select a supplier</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="edit-eta">ETA *</label>
              <input
                id="edit-eta"
                type="datetime-local"
                value={editScheduledFor}
                onChange={(e) => setEditScheduledFor(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="edit-invoice">Invoice number</label>
              <input
                id="edit-invoice"
                type="text"
                maxLength={120}
                value={editInvoice}
                onChange={(e) => setEditInvoice(e.target.value)}
              />
            </div>
            <div className="field full">
              <label htmlFor="edit-notes">Notes</label>
              <input
                id="edit-notes"
                type="text"
                maxLength={500}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
              />
            </div>
          </div>
          <DeliveryLinesEditor
            locationId={delivery.location.id}
            lines={editLines}
            onChange={setEditLines}
          />
          <div className="button-row" style={{ justifyContent: "flex-end" }}>
            <button
              type="button"
              className="button plain"
              onClick={cancelEdit}
              disabled={saving}
            >
              Discard
            </button>
            <button
              type="button"
              className="button"
              onClick={() => void saveEdit()}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      ) : (
        <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3 style={{ margin: 0 }}>Checklist</h3>
            <Link to="/app/deliveries" className="button plain">
              ← Back to deliveries
            </Link>
          </div>
          {canConfirmLines ? (
            <DeliveryScanBox
              delivery={delivery}
              onFound={(line, how) => setFound({ lineId: line.id, how })}
              onUnmatched={setUnmatchedCode}
            />
          ) : null}
          {orderedLines.map((line) => (
            <ConfirmDeliveryLineRow
              key={line.id}
              line={line}
              canConfirm={canConfirmLines}
              highlighted={found?.lineId === line.id}
              unmatchedCode={unmatchedCode}
              onConfirm={(payload, photo) => confirmLine(line.id, payload, photo)}
              onCaptureBarcode={async (barcode) => {
                await captureLineBarcode(line.id, barcode);
                setUnmatchedCode(null);
              }}
              onUndoReceive={
                isOwnerOrManager && delivery.status !== "CANCELED"
                  ? () => {
                      setUndoError(null);
                      setUndoLine(line);
                    }
                  : undefined
              }
            />
          ))}
        </div>
      )}

      {cancelOpen ? (
        <ReasonModal
          title="Cancel this delivery?"
          message={
            delivery.invoice
              ? `Nothing was received from it. The paper it came from${
                  delivery.invoice.invoiceNumber ? ` (${delivery.invoice.invoiceNumber})` : ""
                } becomes free again: you can schedule it again, or delete it.`
              : "Nothing was received from it. A cancelled delivery cannot be reopened."
          }
          reasonMode="none"
          confirmLabel="Cancel delivery"
          cancelLabel="Keep it"
          destructive
          busy={canceling}
          error={cancelError}
          onConfirm={cancelDelivery}
          onClose={() => setCancelOpen(false)}
        />
      ) : null}

      {moveOpen ? (
        <ReasonModal
          title="Move this delivery to another shop?"
          reasonMode="none"
          confirmLabel="Move"
          cancelLabel="Keep it here"
          busy={moveBusy}
          error={moveError}
          onConfirm={moveDelivery}
          onClose={() => setMoveOpen(false)}
        >
          <div className="field">
            <label htmlFor="delivery-move-shop">Shop</label>
            <select
              id="delivery-move-shop"
              value={moveTo}
              disabled={moveBusy}
              onChange={(e) => setMoveTo(e.target.value)}
            >
              <option value="">— Pick a shop —</option>
              {moveShops
                .filter((shop) => shop.isActive && shop.id !== delivery.location.id)
                .map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name} ({shop.code})
                  </option>
                ))}
            </select>
          </div>
        </ReasonModal>
      ) : null}

      {undoLine ? (
        <ReasonModal
          title={`Undo receiving of ${undoLine.item.name}?`}
          message="The units go back out of stock, their batch closes at €0 and the line waits to be received again. Use it for a delivery received by mistake; once every line is waiting, the delivery can be cancelled."
          reasonMode="required"
          reasonLabel="Why?"
          reasonPlaceholder="e.g. received twice, the albarán already brought it"
          minReasonLength={3}
          confirmLabel="Undo receiving"
          destructive
          busy={undoBusy}
          error={undoError}
          onConfirm={undoReceive}
          onClose={() => setUndoLine(null)}
        />
      ) : null}

      {reopenOpen ? (
        <ReasonModal
          title="Reopen the wait for a factura?"
          reasonMode="required"
          reasonLabel="Why?"
          reasonPlaceholder="e.g. the factura turned up"
          minReasonLength={3}
          confirmLabel="Reopen"
          busy={reopenBusy}
          error={reopenError}
          onConfirm={reopenFacturaWait}
          onClose={() => setReopenOpen(false)}
        />
      ) : null}

      {dismissOpen ? (
        <ReasonModal
          title="No factura for this delivery?"
          message="It leaves the waiting list. Its typed purchases keep counting as cost, but their IVA is shown as not deductible. To link a factura later, reopen the wait here first."
          reasonMode="required"
          reasonLabel="Why will no factura come?"
          reasonPlaceholder="e.g. cash purchase at the market"
          minReasonLength={3}
          confirmLabel="Close the wait"
          busy={dismissBusy}
          error={dismissError}
          onConfirm={dismissFacturaWait}
          onClose={() => setDismissOpen(false)}
        />
      ) : null}
    </section>
  );
}

function Stat({
  label,
  value,
  alarm = false,
}: {
  label: string;
  value: string;
  /** Money that is missing reads as missing, not as one more number. */
  alarm?: boolean;
}) {
  return (
    <div>
      <div
        className="muted"
        style={{ fontSize: 12, textTransform: "uppercase" }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 600,
          color: alarm ? "var(--color-danger)" : undefined,
        }}
      >
        {value}
      </div>
    </div>
  );
}
